import MenuIcon from "@mui/icons-material/Menu";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { NotificationDropdown } from "@/components/layout/NotificationDropdown";
import { ProfileMenu } from "@/components/layout/ProfileMenu";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { setTheme, toggleSidebar } from "@/store/appSlice";
import type { ReactNode } from "react";

interface HeaderProps {
  /** Active module / workspace name shown on the left (e.g. Human Resources, HR). */
  moduleLabel?: string;
  /** @deprecated Use moduleLabel */
  title?: string;
  /** Left of header — app launcher, etc. */
  leading?: ReactNode;
  /** Right of header — company switcher, store selector, etc. */
  trailing?: ReactNode;
  extra?: ReactNode;
  onOpenSearch?: () => void;
  onOpenHelp?: () => void;
}

export function Header({
  moduleLabel,
  title,
  leading,
  trailing,
  extra,
  onOpenSearch,
  onOpenHelp,
}: HeaderProps) {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.app.theme);
  const masterAdmin = useAppSelector((s) => s.auth.user?.masterAdmin === true);
  const workspaceLabel = moduleLabel ?? title;

  return (
    <header className="workspace-header" role="banner">
      <div className="workspace-header-start">
        <button
          type="button"
          onClick={() => dispatch(toggleSidebar())}
          className="workspace-header-menu-btn lg:hidden"
          aria-label="Toggle sidebar"
        >
          <MenuIcon sx={{ fontSize: 22 }} />
        </button>
        {leading}
        {workspaceLabel ? (
          <span className="workspace-header-module">{workspaceLabel}</span>
        ) : null}
        {trailing ? (
          <div className="workspace-header-context flex min-w-0 items-center gap-1.5 sm:gap-2">
            {trailing}
          </div>
        ) : null}
      </div>
      <div className="workspace-header-end">
        <div className="workspace-header-tools">
          {extra}
          {onOpenSearch && (
            <Tooltip content="Search (Ctrl+K)">
              <button
                type="button"
                className="workspace-header-ghost-btn"
                onClick={onOpenSearch}
                aria-label="Search (Ctrl+K)"
              >
                <SearchIcon sx={{ fontSize: 22 }} />
              </button>
            </Tooltip>
          )}
          {onOpenHelp && (
            <>
              <Tooltip content="Help">
                <button
                  type="button"
                  className="workspace-header-pill-btn"
                  onClick={onOpenHelp}
                  aria-label="Help"
                >
                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                  <span>Help</span>
                </button>
              </Tooltip>
              <Tooltip content="Help">
                <button
                  type="button"
                  className="workspace-header-ghost-btn sm:hidden"
                  onClick={onOpenHelp}
                  aria-label="Help"
                >
                  <HelpOutlineIcon sx={{ fontSize: 22 }} />
                </button>
              </Tooltip>
            </>
          )}
          <Tooltip content="Toggle theme">
            <button
              type="button"
              className="workspace-header-ghost-btn"
              onClick={() =>
                dispatch(setTheme(theme === "dark" ? "light" : "dark"))
              }
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <LightModeIcon sx={{ fontSize: 22 }} />
              ) : (
                <DarkModeIcon sx={{ fontSize: 22 }} />
              )}
            </button>
          </Tooltip>
          {!masterAdmin && <NotificationDropdown variant="header" />}
        </div>
        <div className="workspace-header-account">
          <ProfileMenu variant="header" />
        </div>
      </div>
    </header>
  );
}
